/**
 * 申請の修正履歴(2026-09-16 改修依頼 C)。
 *
 * 経理修正・承認者の全項目修正(2026-09-17 からどの段階でも)で「いつ・誰が・どの項目を・何から何へ」変えたかを残す。
 * - 保存先は監査ログ(audit_logs)。権限ルールで追記のみ許可され、誰も書き換え・削除できない
 * - 申請の更新は従来とまったく同じ書き込みのまま行い、その直後に履歴を書く。
 *   1回の書き込みにまとめると権限ルールの評価量が増え、式評価の上限で修正そのものが
 *   拒否される恐れがあるため(2026-08-26・09-09・09-10 に同種の事故)。
 *   履歴だけ失敗した場合は、呼び出し側で「履歴の記録に失敗」と必ず表示する
 * - 読めるのは承認者・管理者(権限ルールで取引の監査ログの閲覧は管理者のみ)
 */
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';

/** 修正できる項目と表示名。posting_month などの自動で決まる項目は履歴に出さない */
export const EDIT_FIELD_LABELS = {
  company_id: '会社',
  department_id: '事業部',
  hierarchy_node_id: '拠点/場所',
  account_id: '科目①',
  sub_account_id: '科目②',
  posting_date: '計上日',
  applied_amount: '支払い予定金額',
  amount: '確定金額',
  payment_date: '支払日',
  purchase_date: '購入日',
  payment_method: '支払い方法',
  payer: '支払い者',
  payment_account_id: '支払い口座',
  transfer_fee: '振込手数料',
  cost_type: '固定/変動',
  attachments: '添付',
  counterparty: '支払先',
  description: '摘要',
  note: '備考',
  approver_note: '承認者備考',
} as const;

export type EditField = keyof typeof EDIT_FIELD_LABELS;

export type EditChange = {
  field: EditField;
  label: string;
  before: string | number | null;
  after: string | number | null;
  /** 画面表示用(科目名・事業部名など。保存時点の名前) */
  before_text: string;
  after_text: string;
};

export type EditHistoryEntry = {
  id: string;
  action: 'accounting-fix' | 'approver-edit';
  by: string;
  at: Timestamp | null;
  changes: EditChange[];
};

/** 空欄・null・未設定は同じ「値なし」として比べる */
function normalize(v: unknown): string | number | null {
  if (v === undefined || v === null || v === '') return null;
  return typeof v === 'number' ? v : String(v);
}

/**
 * 変更前(申請)と変更後の値を比べ、変わった項目だけを返す。
 * labels で表示名を差し替えられる(支払い前の amount は「支払い予定金額」、payment_date は「支払い予定日」など)
 */
export function diffEdit(
  before: Record<string, unknown>,
  after: Partial<Record<EditField, unknown>>,
  toText: (field: EditField, value: string | number | null) => string,
  labels: Partial<Record<EditField, string>> = {},
): EditChange[] {
  const changes: EditChange[] = [];
  for (const field of Object.keys(after) as EditField[]) {
    if (!(field in EDIT_FIELD_LABELS)) continue;
    const b = normalize(before[field]);
    const a = normalize(after[field]);
    if (b === a) continue;
    changes.push({
      field,
      label: labels[field] ?? EDIT_FIELD_LABELS[field],
      before: b,
      after: a,
      before_text: toText(field, b),
      after_text: toText(field, a),
    });
  }
  return changes;
}

/**
 * 申請を更新し、変わった項目があれば続けて履歴を残す。
 * 申請の更新に失敗したら例外(従来どおり)。履歴だけ失敗した場合は historySaved=false を返す
 */
export async function saveTransactionEdit(params: {
  transactionId: string;
  patch: Record<string, unknown>;
  changes: EditChange[];
  userId: string;
  action: EditHistoryEntry['action'];
}): Promise<{ historySaved: boolean }> {
  await updateDoc(doc(getDb(), 'transactions', params.transactionId), params.patch);
  return { historySaved: await writeEditHistory(params) };
}

/** 修正履歴だけを書く。変わった項目がなければ何もしない。失敗したら false(例外にはしない) */
export async function writeEditHistory(params: {
  transactionId: string;
  changes: EditChange[];
  userId: string;
  action: EditHistoryEntry['action'];
}): Promise<boolean> {
  if (params.changes.length === 0) return true;
  const before: Record<string, unknown> = {};
  const after: Record<string, unknown> = {};
  for (const c of params.changes) {
    before[c.field] = c.before;
    after[c.field] = c.after;
  }
  try {
    await addDoc(collection(getDb(), 'audit_logs'), {
      target_type: 'transaction',
      target_id: params.transactionId,
      action: params.action,
      by: params.userId,
      at: serverTimestamp(),
      before,
      after,
      changes: params.changes,
    });
    return true;
  } catch (e) {
    console.error('修正履歴の記録に失敗しました', e);
    return false;
  }
}

/** 申請の修正履歴(新しい順)。読む権限がない場合は空を返す */
export async function fetchEditHistory(transactionId: string): Promise<EditHistoryEntry[]> {
  const snap = await getDocs(query(collection(getDb(), 'audit_logs'), where('target_id', '==', transactionId)));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<EditHistoryEntry, 'id'>) }))
    .filter((e) => e.action === 'accounting-fix' || e.action === 'approver-edit')
    .sort((a, b) => (b.at?.toMillis?.() ?? 0) - (a.at?.toMillis?.() ?? 0));
}
